import { v4 as uuid } from 'uuid'
import {
  Block,
  BlockPropsByType,
  BlockType,
  DocletContent
} from '../../../doclets/types'
import { createBlock } from '../../../doclets/registry'

export interface BuilderState {
  content: DocletContent
  selectedBlockId: string | null
}

export type BuilderAction =
  | {
      type: 'add'
      blockType: BlockType
      id?: string
      // Starting props in place of the type's defaults (e.g. a Text preset)
      props?: BlockPropsByType[BlockType]
    }
  | { type: 'update'; id: string; props: BlockPropsByType[BlockType] }
  | { type: 'moveUp'; id: string }
  | { type: 'moveDown'; id: string }
  | { type: 'duplicate'; id: string; newId?: string }
  | { type: 'remove'; id: string }
  | { type: 'toggleHighlight'; id: string }
  | { type: 'toggleFullWidth'; id: string }
  | { type: 'select'; id: string | null }

export const createBuilderState = (
  content: DocletContent = { version: 1, blocks: [] }
): BuilderState => ({
  content,
  selectedBlockId: null
})

export const initialBuilderState: BuilderState = createBuilderState()

const withBlocks = (state: BuilderState, blocks: Block[]): BuilderState => ({
  ...state,
  content: { ...state.content, blocks }
})

const swap = (blocks: Block[], from: number, to: number) => {
  if (from < 0 || to < 0 || from >= blocks.length || to >= blocks.length) {
    return blocks
  }
  const next = [...blocks]
  ;[next[from], next[to]] = [next[to], next[from]]
  return next
}

// `id` / `newId` are injectable for tests; callers normally omit them.
export const builderReducer = (
  state: BuilderState,
  action: BuilderAction
): BuilderState => {
  const { blocks } = state.content
  const index =
    'id' in action && action.id
      ? blocks.findIndex(block => block.id === action.id)
      : -1

  switch (action.type) {
    case 'add': {
      const created = createBlock(action.blockType, action.id ?? uuid())
      const block = action.props ? { ...created, props: action.props } : created
      return {
        ...withBlocks(state, [...blocks, block]),
        selectedBlockId: block.id
      }
    }
    case 'update':
      if (index < 0) return state
      return withBlocks(
        state,
        blocks.map(block =>
          block.id === action.id ? { ...block, props: action.props } : block
        )
      )
    case 'moveUp':
      return withBlocks(state, swap(blocks, index, index - 1))
    case 'moveDown':
      if (index < 0) return state
      return withBlocks(state, swap(blocks, index, index + 1))
    case 'duplicate': {
      if (index < 0) return state
      const copy = {
        ...blocks[index],
        id: action.newId ?? uuid(),
        props: { ...blocks[index].props }
      }
      const next = [...blocks]
      next.splice(index + 1, 0, copy)
      return { ...withBlocks(state, next), selectedBlockId: copy.id }
    }
    case 'remove':
      if (index < 0) return state
      return {
        ...withBlocks(
          state,
          blocks.filter(block => block.id !== action.id)
        ),
        selectedBlockId:
          state.selectedBlockId === action.id ? null : state.selectedBlockId
      }
    case 'toggleHighlight':
      if (index < 0) return state
      return withBlocks(
        state,
        blocks.map(block =>
          block.id === action.id
            ? { ...block, highlighted: !block.highlighted }
            : block
        )
      )
    case 'toggleFullWidth':
      if (index < 0) return state
      return withBlocks(
        state,
        blocks.map(block =>
          block.id === action.id
            ? { ...block, fullWidth: !block.fullWidth }
            : block
        )
      )
    case 'select':
      return { ...state, selectedBlockId: action.id }
    default:
      return state
  }
}
